import { useMemo, useState } from 'react';

import { apiClient } from '@infrastructure/api/client';
import { AdminListRepository } from '@infrastructure/api/AdminListRepository';
import { DataTable, type DataTableColumn } from '@app/components/data-table/DataTable';
import { formatDateTime } from '@app/lib/format';

interface AIUsageRow {
  id: number;
  timestamp: string;
  model_name: string;
  question_type: string;
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  cost_cents: number;
}

const repo = new AdminListRepository<AIUsageRow>(apiClient, 'ai-usage');

const columns: DataTableColumn<AIUsageRow>[] = [
  { id: 'timestamp', header: 'When', accessor: (r) => formatDateTime(r.timestamp) },
  { id: 'model_name', header: 'Model', accessor: (r) => <code className="font-mono text-xs">{r.model_name}</code> },
  { id: 'question_type', header: 'Type', accessor: 'question_type' },
  { id: 'input_tokens', header: 'In tokens', accessor: 'input_tokens' },
  { id: 'output_tokens', header: 'Out tokens', accessor: 'output_tokens' },
  { id: 'total_tokens', header: 'Total', accessor: 'total_tokens' },
  // Stored as whole cents rounded down, so a small call (a fraction of a cent) shows as < $0.01
  {
    id: 'cost_cents', header: 'Cost',
    accessor: (r) => (r.cost_cents > 0 ? `$${(r.cost_cents / 100).toFixed(2)}` : '< $0.01'),
  },
];

export function AIUsagePage() {
  const [modelFilter, setModelFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const filters = useMemo<Record<string, string>>(() => {
    const f: Record<string, string> = {};
    if (modelFilter) f.model_name = modelFilter;
    if (typeFilter) f.question_type = typeFilter;
    return f;
  }, [modelFilter, typeFilter]);

  return (
    <section>
      <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
        <h2 className="text-lg font-semibold">
          AI Usage / Cost
          <span className="ml-2 text-xs font-normal text-slate-500">website sales assistant calls</span>
        </h2>
        <div className="flex flex-wrap gap-2 text-sm">
          <input
            type="text"
            placeholder="Model name (exact)"
            value={modelFilter}
            onChange={(e) => setModelFilter(e.target.value)}
            className="rounded border px-2 py-1"
          />
          <input
            type="text"
            placeholder="Question type"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="rounded border px-2 py-1"
          />
        </div>
      </div>
      <DataTable<AIUsageRow>
        columns={columns}
        fetcher={(p) => repo.list({ ...p, filters })}
        pagination="offset"
        defaultPageSize={50}
        rowKey={(r) => String(r.id)}
        emptyState={<span data-testid="datatable-empty">No AI usage recorded.</span>}
      />
    </section>
  );
}
